import { discountPercent, escapeHtml, formatMenuPrice, formatMenuPriceParts, safeUrl } from '../format.js';
import type { MenuColumn, MenuPage } from '../paginate.js';
import type {
  MenuBoardContent,
  MenuItemContent,
  MenuOrientation,
  MenuSection,
  MenuTheme,
} from '../types.js';

/** Everything a design needs to draw one page, with the escaping already taken care of. */
export interface RenderContext {
  board: MenuBoardContent;
  page: MenuPage;
  pageIndex: number;
  pageCount: number;
  orientation: MenuOrientation;
  showEn: boolean;
  showAr: boolean;
  /** A trusted URL for a media id, or null when it is missing or not cached. */
  img: (mediaId: string | null | undefined) => string | null;
}

/**
 * A look for a design: the same layout in other colours and fonts, and, where the design's CSS
 * keys off `.mb-v-<id>`, other shapes (ribbons, photo frames, dividers).
 */
export const MENU_CUISINES = ['burgers', 'pizza', 'pasta', 'cafe', 'drinks'] as const;
export type MenuCuisine = (typeof MENU_CUISINES)[number];

/**
 * A matching set: designs that share one look in different layouts, so the screens of one shop
 * (burgers on one, sides on the next, drinks on the last) clearly belong together.
 */
export interface MenuFamily {
  id: string;
  name: string;
  /** What sets this layout apart from the others in its set: "Four columns", "Hero and list". */
  layout: string;
}

export interface MenuStyleVariant {
  id: string;
  name: string;
  theme: Partial<MenuTheme>;
}

export interface MenuTemplate {
  id: string;
  name: string;
  description: string;
  defaultTheme: MenuTheme;
  /** At least one; the first is the design's default look. */
  variants: MenuStyleVariant[];
  /** What the design suits, for filtering the gallery. */
  cuisines: MenuCuisine[];
  /** The matching set the design belongs to; absent for stand-alone designs. */
  family?: MenuFamily;
  /** Which board-level parts the design shows, so the editor only offers those. */
  uses: {
    featured: boolean;
    sectionImages: boolean;
    brand: boolean;
    /** What each decoration picture is for, in order; empty when the design has none. */
    decorations: string[];
  };
  paginate: (board: MenuBoardContent, orientation: MenuOrientation) => MenuPage[];
  render: (ctx: RenderContext) => string;
  /** Scoped under `.mb-t-<id>`. */
  css: string;
}

/**
 * English and/or Arabic versions of a string as elements of class `cls`. On a bilingual board
 * the Arabic line follows and is marked `mb-2nd` so designs can set it smaller. An Arabic-only
 * board falls back to English when there is no translation, rather than leaving a gap.
 */
export function bilingual(
  ctx: RenderContext,
  en: string | null | undefined,
  ar: string | null | undefined,
  cls: string,
  tag = 'p',
): string {
  const enText = en?.trim() || null;
  const arText = ar?.trim() || null;
  const parts: string[] = [];
  if (ctx.showEn && enText) parts.push(`<${tag} class="${cls}">${escapeHtml(enText)}</${tag}>`);
  if (ctx.showAr && arText) {
    const second = ctx.showEn && enText ? ' mb-2nd' : '';
    parts.push(`<${tag} class="${cls} mb-ar${second}" dir="rtl" lang="ar">${escapeHtml(arText)}</${tag}>`);
  }
  if (parts.length === 0 && enText) parts.push(`<${tag} class="${cls}">${escapeHtml(enText)}</${tag}>`);
  return parts.join('');
}

/** Ingredient lists as one line each: "Beef · Cheddar · Lettuce". */
export function ingredients(ctx: RenderContext, item: MenuItemContent, cls: string): string {
  if (!ctx.board.settings.showIngredients) return '';
  const join = (values: string[]) => values.map((v) => v.trim()).filter(Boolean).join(' · ') || null;
  return bilingual(ctx, join(item.ingredients), join(item.ingredientsAr), cls);
}

export function description(ctx: RenderContext, item: MenuItemContent, cls: string): string {
  if (!ctx.board.settings.showDescriptions) return '';
  return bilingual(ctx, item.description, item.descriptionAr, cls);
}

export function price(ctx: RenderContext, amount: number): string {
  return escapeHtml(formatMenuPrice(amount, ctx.board.settings.currency));
}

/** A price with the decimals raised and the symbol set small: 10⁹⁹. */
export function raisedPrice(ctx: RenderContext, amount: number, cls = 'mb-rp'): string {
  const p = formatMenuPriceParts(amount, ctx.board.settings.currency);
  const symbol = `<span class="${cls}-s">${escapeHtml(p.symbol)}</span>`;
  return (
    `<span class="${cls}" dir="ltr">` +
    (p.before ? symbol : '') +
    `<span class="${cls}-w">${escapeHtml(p.whole)}</span>` +
    (p.fraction ? `<span class="${cls}-f">${escapeHtml(p.fraction)}</span>` : '') +
    (p.before ? '' : symbol) +
    '</span>'
  );
}

/**
 * The selling price of an item in a design's own classes: the discounted price with the
 * original struck through, or the plain price. Items with variants are drawn by the caller.
 */
export function sellingPrice(ctx: RenderContext, item: MenuItemContent, cls: string): string {
  const percent = discountPercent(item);
  if (percent === null) return `<span class="${cls}" dir="ltr">${price(ctx, item.price)}</span>`;
  return (
    `<span class="${cls}" dir="ltr">${price(ctx, item.discountPrice!)}</span>` +
    `<s class="${cls}-old" dir="ltr">${price(ctx, item.price)}</s>`
  );
}

export function badge(ctx: RenderContext, item: MenuItemContent, cls: string): string {
  const percent = discountPercent(item);
  const parts: string[] = [];
  if (percent !== null) parts.push(`<span class="${cls}" dir="ltr">-${percent}%</span>`);
  const label = ctx.showEn ? item.badge || item.badgeAr : item.badgeAr || item.badge;
  // `dir=auto` keeps "3 TACOS" in its own order on a right-to-left board.
  if (label?.trim()) parts.push(`<span class="${cls}" dir="auto">${escapeHtml(label.trim())}</span>`);
  return parts.join('');
}

export function calories(item: MenuItemContent, cls: string): string {
  return item.calories != null ? `<span class="${cls}" dir="ltr">${item.calories} cal</span>` : '';
}

export function variantLabel(ctx: RenderContext, label: string, labelAr: string | null): string {
  const text = ctx.showEn ? label : labelAr?.trim() || label;
  return escapeHtml(text);
}

export function image(ctx: RenderContext, mediaId: string | null | undefined, cls: string): string {
  const url = safeUrl(ctx.img(mediaId));
  return url ? `<img class="${cls}" src="${escapeHtml(url)}" alt="">` : '';
}

/** Section heading and subtitle; empty for an untitled section. */
export function sectionTitle(ctx: RenderContext, section: MenuSection, cls: string): string {
  return bilingual(ctx, section.title, section.titleAr, cls);
}

export function sectionSubtitle(ctx: RenderContext, section: MenuSection, cls: string): string {
  return bilingual(ctx, section.subtitle, section.subtitleAr, cls);
}

export function hasTitle(section: MenuSection): boolean {
  return Boolean(section.title?.trim() || section.titleAr?.trim());
}

/**
 * Bilingual boards stack two lines where others have one, and Arabic lines are set taller than
 * Latin ones; block costs grow to match.
 */
export function languageFactor(board: MenuBoardContent): number {
  const language = board.settings.language;
  return language === 'both' ? 1.4 : language === 'ar' ? 1.15 : 1;
}

/** Arabic display lines need about a quarter more height than Latin capitals. */
export const ARABIC_LINE = 1.25;

/** Columns that all cost the same, for the common case. */
export function evenColumns(count: number, column: MenuColumn): MenuColumn[] {
  return Array.from({ length: count }, () => column);
}

/**
 * How much to shrink a display line so it fits on one line: 1 when it already does, never
 * below `min`. `maxChars` is how many characters fit at full size.
 */
export function fitScale(text: string | null | undefined, maxChars: number, min = 0.55): number {
  const length = text?.trim().length ?? 0;
  return length > maxChars ? Math.max(min, maxChars / length) : 1;
}

/** Lines a paragraph wraps to, capped at the design's line clamp. */
export function lineCount(text: string | null | undefined, charsPerLine: number, max: number): number {
  const length = text?.trim().length ?? 0;
  return length === 0 ? 0 : Math.min(max, Math.ceil(length / charsPerLine));
}

/* ------------------------------------------------------------------ list designs */

/**
 * The selling price(s) of an item in a design's classes: sizes side by side, or the price with
 * the original struck through under it when discounted.
 */
export function priceBlock(ctx: RenderContext, item: MenuItemContent, p: string): string {
  if (item.variants.length) {
    return `<span class="${p}-sizes">${item.variants
      .map(
        (v) =>
          `<span class="${p}-size"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <span class="${p}-price" dir="ltr">${price(ctx, v.price)}</span></span>`,
      )
      .join('')}</span>`;
  }
  const percent = discountPercent(item);
  if (percent === null) return `<span class="${p}-price" dir="ltr">${price(ctx, item.price)}</span>`;
  return `<span class="${p}-pricebox"><span class="${p}-price" dir="ltr">${price(ctx, item.discountPrice!)}</span><s class="${p}-old" dir="ltr">${price(ctx, item.price)}</s></span>`;
}

/**
 * One menu line: name, a leader running to the price, then the second-language name and the
 * description underneath. Classes are `<p>-item`, `-line`, `-name`, `-leader`, `-name2`, `-desc`.
 */
export function listItem(ctx: RenderContext, item: MenuItemContent, p: string, leader = true): string {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<span class="${p}-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</span>`
    : `<span class="${p}-name">${escapeHtml(item.title)}</span>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<p class="${p}-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  const badges = badge(ctx, item, `${p}-badge`);
  return [
    `<div class="${p}-item">`,
    `<div class="${p}-line">${name}${badges ? `<span class="${p}-badges">${badges}</span>` : ''}${leader ? `<span class="${p}-leader"></span>` : ''}${priceBlock(ctx, item, p)}</div>`,
    second,
    description(ctx, item, `${p}-desc`),
    '</div>',
  ].join('');
}

/**
 * Height of a `listItem` in em at the reference size: the name line, a second-language line on a
 * bilingual board, wrapped description lines (two at most) and the gap after it.
 */
export function listItemHeight(
  board: MenuBoardContent,
  item: MenuItemContent,
  opts: { nameEm: number; descEm: number; perLine: number; gap: number },
): number {
  const en = board.settings.language !== 'ar';
  const ar = board.settings.language !== 'en';
  const name = opts.nameEm * 1.2 + (en && ar && item.titleAr ? opts.nameEm * 0.85 * 1.2 * ARABIC_LINE : 0);
  const desc = board.settings.showDescriptions
    ? (en || !item.descriptionAr ? lineCount(item.description, opts.perLine, 2) : 0) +
      (ar ? lineCount(item.descriptionAr, opts.perLine, 2) : 0)
    : 0;
  return name + desc * opts.descEm * 1.3 + opts.gap;
}

/** Sections flowed into `count` equal columns of list items under headings. */
export function listColumns(
  board: MenuBoardContent,
  opts: {
    count: number;
    capacity: number;
    heading: number;
    item: { nameEm: number; descEm: number; perLine: number; gap: number };
    /** Extra height a block takes, such as its section picture. */
    extra?: (section: MenuSection, continued: boolean) => number;
  },
): MenuColumn[] {
  const headingHeight = (section: MenuSection) =>
    hasTitle(section)
      ? opts.heading * (board.settings.language === 'both' && section.titleAr ? 1.55 : 1) +
        (section.subtitle || section.subtitleAr ? 1.6 : 0)
      : 0;
  const column: MenuColumn = {
    capacity: opts.capacity,
    blockCost: (section, items, continued) =>
      headingHeight(section) +
      (opts.extra?.(section, continued) ?? 0) +
      items.reduce((sum, item) => sum + listItemHeight(board, item, opts.item), 0) +
      1.5,
  };
  return Array.from({ length: opts.count }, () => column);
}

/** The items a design shows as photos on this page, in order, up to `max`. */
export function pagePhotos(ctx: RenderContext, max: number): MenuItemContent[] {
  return ctx.page.columns
    .flat()
    .flatMap((b) => b.items)
    .filter((i) => i.imageMediaId)
    .slice(0, max);
}

/**
 * Base styles for `listItem` under `scope` (a `.mb-t-<id>` selector): the line, leader, sizes,
 * discount and description. Designs then set fonts and colours on top.
 */
export function listCss(scope: string, p: string, o: { name: string; price: string; desc: string; leader: string }): string {
  return `
${scope} .${p}-item{break-inside:avoid}
${scope} .${p}-line{display:flex;align-items:last baseline;gap:.5em}
${scope} .${p}-name{font-size:${o.name};line-height:1.15;flex:0 1 auto;min-width:0;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${scope} .${p}-leader{flex:1 0 1em;min-width:1em;border-bottom:${o.leader};transform:translateY(-.3em)}
${scope} .${p}-price{font-size:${o.price};white-space:nowrap}
${scope} .${p}-pricebox{display:flex;flex-direction:column;align-items:flex-end;line-height:1.05}
${scope} .${p}-old{font-size:.8em;opacity:.55;white-space:nowrap}
${scope} .${p}-sizes{display:flex;gap:.8em;white-space:nowrap}
${scope} .${p}-size small{font-size:.75em;opacity:.75;margin-inline-end:.15em}
${scope} .${p}-size .${p}-price{font-size:calc(${o.price} * .9)}
${scope} .${p}-badges{display:flex;gap:.3em;flex-shrink:0}
${scope} .${p}-badge{font-size:.7em;font-weight:800;padding:.15em .5em;border-radius:99em;background:var(--mb-accent);color:var(--mb-bg);white-space:nowrap}
${scope} .${p}-name2{font-weight:700;opacity:.9;margin-top:.1em}
${scope} .${p}-desc{font-size:${o.desc};opacity:.75;margin-top:.2em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
`;
}
